import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import { useAppContext } from '../contexts/Storage';
import { useRecords } from '../hooks/useRecords';
import {
  TransactionsList,
  TransactionDetail,
  DeleteTransactionModal,
  useTransactions,
} from '../components/Transaction';

const TransactionsPage: React.FC = () => {
  const { setNavLinks } = useNavLinksContext();
  const { selectedRecord } = useAppContext();

  const {
    // Data
    transactions,
    pagination,
    selectedTransaction,
    hasRecordId,

    // Loading states
    isLoading,
    isDeleting,

    // Filter state and handlers
    filters,
    handleFilterChange,

    // Entity filter state and handlers
    entityFilterValues,
    handleEntityFilterValueChange,
    handleEntityFilterSelect,
    clearEntityFilter,
    clearAllFilters,
    hasActiveFilters,
    createFetchSuggestions,

    // Transaction actions
    handleSelectTransaction,
    handleCloseDetail,
    handleEditTransaction,

    // Delete modal
    deleteModalState,
    openDeleteModal,
    closeDeleteModal,
    handleConfirmDelete,
  } = useTransactions({ recordId: selectedRecord?.id });

  // Fetch users for the current record
  const { getRecordUsers } = useRecords();
  const [users, setUsers] = useState<Array<{ id: number; name: string; imageUrl?: string }>>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const fetchRecordUsers = useCallback(async () => {
    if (!selectedRecord?.id) return;

    setIsLoadingUsers(true);
    try {
      const result = await getRecordUsers(selectedRecord.id);
      if (result.success && result.data) {
        // The API returns users array in result.data.users
        // Type the response based on what we see in ManagePermissionsModal
        interface ApiUser {
          id: number;
          username: string;
          email: string;
          first_name: string;
          last_name: string;
          image_url?: string;
          pivot: {
            can_read: number;
            can_write: number;
          };
        }

        const apiUsers = (result.data as any).users as ApiUser[];
        const transformedUsers = apiUsers.map((apiUser) => ({
          id: apiUser.id,
          name: apiUser.first_name && apiUser.last_name
            ? `${apiUser.first_name} ${apiUser.last_name}`
            : apiUser.username || `User ${apiUser.id}`,
          imageUrl: apiUser.image_url,
        }));
        setUsers(transformedUsers);
      }
    } catch (error) {
      console.error('Failed to fetch record users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [selectedRecord?.id, getRecordUsers]);

  useEffect(() => {
    if (selectedRecord?.id) {
      fetchRecordUsers();
    }
  }, [selectedRecord?.id, fetchRecordUsers]);

  const handleUserFilterChange = useCallback((userId: number | undefined) => {
    handleFilterChange('created_by_user_id', userId);
  }, [handleFilterChange]);

  // Set navigation links for the Transactions page
  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/records', label: 'Books' },
        ...(selectedRecord ? [{ to: '/records/transactions', label: selectedRecord.record_name || 'Transactions' }] : []),
      ],
    });
  }, [setNavLinks, selectedRecord]);

  // Show message if no record is selected
  if (!selectedRecord) {
    return (
      <div className="h-shink-from-navbar h-fill-from-navbar pl-2 bg-bg_grey_light text-dark overflow-y-scroll">
        <div className="bg-light rounded-lg p-8 text-center">
          <p className="text-grey_dark mb-4">Please select a record to view transactions.</p>
          <Link
            to="/records"
            className="text-primary hover:text-primary_dark underline"
          >
            Go to Records
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar pl-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="">
        {/* Show message if no record selected (shouldn't happen due to check above) */}
        {!hasRecordId && (
          <div className="bg-light rounded-lg p-8 text-center">
            <p className="text-grey_dark">Please select a record to view transactions.</p>
          </div>
        )}

        {/* Two-column layout for desktop, stacked for mobile */}
        {hasRecordId && (
          <div className="flex flex-col lg:flex-row gap-2">
            {/* Left column: Transaction list - hidden on mobile when detail is shown */}
            <div
              className={`transition-all duration-300 ease-out ${selectedTransaction ? 'lg:w-3/5 hidden lg:block' : 'w-full'
                }`}
            >
              <TransactionsList
                transactions={transactions}
                loading={isLoading}
                filter={filters}
                onFilterChange={handleFilterChange}
                onEdit={handleEditTransaction}
                onDelete={openDeleteModal}
                onTransactionClick={handleSelectTransaction}
                isSelectedTransactionVisible={!!selectedTransaction}
                recordName={selectedRecord.record_name}
                pagination={pagination}
                entityFilterValues={entityFilterValues}
                onEntityFilterValueChange={handleEntityFilterValueChange}
                onEntityFilterSelect={handleEntityFilterSelect}
                onClearEntityFilter={clearEntityFilter}
                onClearAllFilters={clearAllFilters}
                hasActiveFilters={hasActiveFilters}
                createFetchSuggestions={createFetchSuggestions}
                users={users}
                onUserFilterChange={handleUserFilterChange}
              />
            </div>

            {/* Right column: Transaction detail or empty state */}
            <div
              className={`transition-all duration-300 ease-out ${selectedTransaction ? 'lg:w-2/5' : 'lg:w-0 hidden lg:block'
                }`}
            >
              {selectedTransaction && (
                <TransactionDetail
                  transaction={selectedTransaction}
                  onClose={handleCloseDetail}
                  onEdit={handleEditTransaction}
                  onDelete={openDeleteModal}
                />
              )}
            </div>
          </div>
        )}

        {/* Delete Transaction Modal */}
        <DeleteTransactionModal
          isOpen={deleteModalState.isOpen}
          onClose={closeDeleteModal}
          transaction={deleteModalState.transaction}
          onConfirmDelete={handleConfirmDelete}
          isDeleting={isDeleting}
        />
      </div>
    </div>
  );
};

export default TransactionsPage;
